import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Hash, Layers, Tag as TagIcon, Trash2 } from "lucide-react";
import type { Workspace } from "@/hooks/useWorkspace";

export type NamedItem = { id: string; name: string };

type Props = {
  selectedCount: number;
  allSelected: boolean;
  bulkBusy: boolean;
  tags: NamedItem[];
  topics: NamedItem[];
  newTagName: string;
  newTopicName: string;
  onToggleSelectAll: () => void;
  onNewTagNameChange: (v: string) => void;
  onNewTopicNameChange: (v: string) => void;
  onCreateAndTag: (name: string) => unknown;
  onCreateAndAddTopic: (name: string) => unknown;
  onAddTag: (tagId: string) => unknown;
  onAddTopic: (topicId: string) => unknown;
  onDelete: () => unknown;
  /** When true, hide the Topic affordance entirely (workspace has Codex toggled off). */
  hideTopics?: boolean;
  /** When true, suppress the "New topic…" input — Topics are Codex-managed only. */
  topicsReadOnly?: boolean;
  /** Optional: when provided, renders a "Move to workspace" action. */
  workspaces?: Workspace[];
  onMoveToWorkspace?: (workspaceId: string) => unknown;
};

export const ExcerptsBulkBar = ({
  selectedCount,
  allSelected,
  bulkBusy,
  tags,
  topics,
  newTagName,
  newTopicName,
  onToggleSelectAll,
  onNewTagNameChange,
  onNewTopicNameChange,
  onCreateAndTag,
  onCreateAndAddTopic,
  onAddTag,
  onAddTopic,
  onDelete,
  hideTopics,
  topicsReadOnly,
  workspaces,
  onMoveToWorkspace,
}: Props) => {
  const disabled = selectedCount === 0 || bulkBusy;
  const showMove = !!workspaces && workspaces.length > 1 && !!onMoveToWorkspace;
  return (
    <div className="sticky top-0 z-20 -mx-4 sm:mx-0 px-3 sm:px-4 py-2 sm:py-3 rounded-none sm:rounded-md border-y sm:border border-border bg-background/95 backdrop-blur shadow-soft flex flex-wrap items-center gap-2 sm:gap-3">
      <button
        type="button"
        onClick={onToggleSelectAll}
        className="flex items-center gap-2 text-sm font-medium"
      >
        <Checkbox checked={allSelected} />
        <span>{selectedCount} selected</span>
      </button>
      <div className="ml-auto flex flex-wrap gap-2">
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="h-9" disabled={disabled}>
              <TagIcon className="h-4 w-4" /> Tag
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-64 space-y-2">
            <p className="text-xs font-medium text-muted-foreground">Add tag to selection</p>
            <Input
              placeholder="New tag…"
              value={newTagName}
              onChange={(e) => onNewTagNameChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") onCreateAndTag(newTagName);
              }}
            />
            {tags.length > 0 && (
              <div className="max-h-48 overflow-y-auto -mx-1">
                {tags.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => onAddTag(t.id)}
                    className="block w-full rounded px-2 py-1 text-left text-sm hover:bg-muted"
                  >
                    {t.name}
                  </button>
                ))}
              </div>
            )}
          </PopoverContent>
        </Popover>
        {!hideTopics && (
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="h-9" disabled={disabled}>
                <Hash className="h-4 w-4" /> Topic
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-64 space-y-2">
              <p className="text-xs font-medium text-muted-foreground">Add topic to selection</p>
              {!topicsReadOnly && (
                <Input
                  placeholder="New topic…"
                  value={newTopicName}
                  onChange={(e) => onNewTopicNameChange(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") onCreateAndAddTopic(newTopicName);
                  }}
                />
              )}
              {topics.length > 0 ? (
                <div className="max-h-48 overflow-y-auto -mx-1">
                  {topics.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => onAddTopic(c.id)}
                      className="block w-full rounded px-2 py-1 text-left text-sm hover:bg-muted"
                    >
                      {c.name}
                    </button>
                  ))}
                </div>
              ) : topicsReadOnly ? (
                <p className="text-xs text-muted-foreground">
                  No topics yet. Add them in Codex → Framework.
                </p>
              ) : null}
            </PopoverContent>
          </Popover>
        )}
        {showMove ? (
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="h-9" disabled={disabled}>
                <Layers className="h-4 w-4" /> Move
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-64 space-y-2">
              <p className="text-xs font-medium text-muted-foreground">Move selection to workspace</p>
              <div className="max-h-48 overflow-y-auto -mx-1">
                {workspaces!.map((w) => (
                  <button
                    key={w.id}
                    type="button"
                    onClick={() => onMoveToWorkspace!(w.id)}
                    className="block w-full rounded px-2 py-1 text-left text-sm hover:bg-muted"
                  >
                    {w.workspace_label || w.display_name || "Workspace"}
                  </button>
                ))}
              </div>
            </PopoverContent>
          </Popover>
        ) : null}
        <Button
          variant="destructive"
          size="sm"
          className="h-9"
          onClick={onDelete}
          disabled={disabled}
        >
          <Trash2 className="h-4 w-4" /> Delete
        </Button>
      </div>
    </div>
  );
};
